import { useEffect, useLayoutEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const STORAGE_PREFIX = 'pulse.listFilters.';

/**
 * Persists the list page query string in sessionStorage while on `listPathname`,
 * and restores it when the user returns with no query (e.g. sidebar link).
 * Detail routes must not call this (only list components).
 */
export function usePersistListFiltersSession(
  listPathname: string,
  storageKey: string
): void {
  const location = useLocation();
  const navigate = useNavigate();
  const fullKey = `${STORAGE_PREFIX}${storageKey}`;

  useLayoutEffect(() => {
    if (location.pathname !== listPathname) return;
    if (location.search && location.search.length > 1) return;
    let stored: string;
    try {
      stored = sessionStorage.getItem(fullKey) ?? '';
    } catch {
      return;
    }
    if (!stored) return;
    const search = stored.startsWith('?') ? stored : `?${stored}`;
    if (search.length <= 1) return;
    navigate(`${listPathname}${search}`, { replace: true });
  }, [location.pathname, location.search, listPathname, fullKey, navigate]);

  useEffect(() => {
    if (location.pathname !== listPathname) return;
    try {
      sessionStorage.setItem(fullKey, location.search || '');
    } catch {
      /* private mode / quota */
    }
  }, [location.pathname, location.search, listPathname, fullKey]);
}
